/**
 * BillingSuccess — Stripe subscription success landing page.
 *
 * Same polling pattern as CheckoutSuccess but copy is tailored to the
 * "Dealer Upgrade" flow. Polls `/api/stripe/status/:session_id` until
 * the webhook transitions the payment transaction to SUCCEEDED, then
 * shows a "You're a Dealer" confirmation.
 */
import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BadgeCheck, LayoutDashboard } from "lucide-react";
import { api, extractApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardBody } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";

type Phase = "polling" | "success" | "expired" | "error" | "unauthenticated";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();

  const [phase, setPhase] = React.useState<Phase>("polling");
  const [attempts, setAttempts] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!sessionId) {
      setPhase("error");
      setError("Missing session_id in URL.");
      return;
    }
    let cancelled = false;
    let count = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/stripe/status/${sessionId}`);
        if (cancelled) return;
        const status = r.data?.status;
        if (status === "SUCCEEDED") {
          // Pull the new role/subscription into auth state.
          try {
            await refresh();
          } catch {
            /* non-fatal -- page still renders the success copy */
          }
          setPhase("success");
          return;
        }
        if (status === "EXPIRED") {
          setPhase("expired");
          return;
        }
        count += 1;
        setAttempts(count);
        if (count >= 8) {
          setPhase("expired");
          return;
        }
        setTimeout(poll, 1500);
      } catch (err: any) {
        if (cancelled) return;
        const code = err?.response?.status;
        if (code === 401 || code === 403) {
          setPhase("unauthenticated");
          return;
        }
        if (code === 404) {
          count += 1;
          setAttempts(count);
          if (count >= 8) {
            setPhase("expired");
            return;
          }
          setTimeout(poll, 1500);
          return;
        }
        setError(extractApiError(err));
        setPhase("error");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, refresh]);

  return (
    <div className="mx-auto max-w-xl px-4 py-12 md:py-20">
      <Card elevated data-testid="billing-success-card">
        <CardBody className="text-center py-10">
          {phase === "polling" && (
            <>
              <div className="h-12 w-12 rounded-full border-4 border-surface-border border-t-brand animate-spin mx-auto mb-5" />
              <h1 className="text-2xl font-black mb-2">Activating your Dealer access</h1>
              <p className="text-ink-muted">
                Waiting on Stripe… {attempts > 0 && `(${attempts}/8)`}
              </p>
            </>
          )}
          {phase === "success" && (
            <>
              <BadgeCheck className="h-14 w-14 text-brand mx-auto mb-5" aria-hidden />
              <h1
                className="text-3xl font-black mb-3"
                data-testid="billing-success-heading"
              >
                You're a Dealer
              </h1>
              <p className="text-ink-muted mb-6">
                Your subscription is active. Enjoy the lower 5% commission, the
                weekly featured-dealer rotation, and your public storefront.
              </p>
              <div className="flex flex-col gap-3">
                <Link to="/profile">
                  <Button
                    variant="primary"
                    size="lg"
                    fullWidth
                    leadingIcon={<LayoutDashboard className="h-5 w-5" />}
                  >
                    Go to my dashboard
                  </Button>
                </Link>
                <Link to="/sell">
                  <Button variant="outline" size="lg" fullWidth>
                    Post my first listing
                  </Button>
                </Link>
              </div>
            </>
          )}
          {phase === "expired" && (
            <>
              <h1 className="text-2xl font-black mb-3">Still processing</h1>
              <p className="text-ink-muted mb-6">
                Stripe is taking longer than usual. Refresh this page in a
                minute — we won't double-charge you.
              </p>
              <Link to="/profile">
                <Button variant="primary" size="lg" fullWidth>
                  My dashboard
                </Button>
              </Link>
            </>
          )}
          {phase === "unauthenticated" && (
            <>
              <h1 className="text-2xl font-black mb-3">Please sign in to continue</h1>
              <p className="text-ink-muted mb-6">
                Your payment has been received by Stripe. Sign in to activate
                your Dealer status.
              </p>
              <Link
                to={`/login?redirect=/billing/success?session_id=${sessionId ?? ""}`}
              >
                <Button variant="primary" size="lg" fullWidth>
                  Sign in
                </Button>
              </Link>
            </>
          )}
          {phase === "error" && (
            <>
              <h1 className="text-2xl font-black mb-3 text-brand">Something went wrong</h1>
              <p className="text-ink-muted mb-6">{error ?? "Please try again."}</p>
              <Link to="/profile">
                <Button variant="outline" size="lg" fullWidth>
                  Back to dashboard
                </Button>
              </Link>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
